import DocumentSchema from '@components/DocumentSchema';
import type { Table } from 'schema-dts';
import Tooltip from '@/components/Tooltip';
import {
  TooltipName,
  TipContainer,
  AnalystDataTable,
  TextColor,
  TableWrapper,
} from './styled';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';
import type { PriceData } from '../../queryPriceData';
import type { PriceTargetData } from '../../queryPriceTargets';
import { format } from 'date-fns';
import { formatterCurrency, formatterPercentage } from '@/utilities/format';
import {
  getValidPrice,
  getPriceGrowthData,
  getCurrentPrice,
  getGrowthColor,
} from '../../utils';
import { subYears } from 'date-fns';
import { Grid, useTheme } from '@simplywallst/ui-core';
import { daysBack, dispersionThreshold } from '../../constants';
import { CompanyReportTableRow } from '@/pages/CompanyReport/styled';
import { useTranslation } from '@/hooks/useTranslation';
import { useValuationData } from '@/pages/CompanyReport/components/Valuation';

interface Props {
  tickerSymbol: string;
  currencyISO: string;
  priceData: PriceData['priceData'];
  priceTargetData: PriceTargetData['priceTargetData'];
  analystData: PriceTargetData['analystData'];
}
export const AnalystPriceTargetTable = ({
  tickerSymbol,
  currencyISO,
  priceData,
  priceTargetData,
  analystData,
}: Props) => {
  const theme = useTheme();
  const getTerm = useGlossaryTerms();
  const valuationData = useValuationData();
  const t = useTranslation();

  const reversePriceTarget = priceTargetData.slice().reverse();
  return (
    <>
      <DocumentSchema<Table>
        item={{
          '@context': 'https://schema.org',
          '@type': 'Table',
          headline: t(
            'page.companyReport.valuation.analystPriceTarget.table.heading',
            {
              tickerSymbol,
            }
          ),
          about: t(
            'page.companyReport.valuation.analystPriceTarget.table.description',
            {
              tickerSymbol,
            }
          ),
        }}
      />
      <TableWrapper data-cy-id="analyst-price-target-table">
        <AnalystDataTable data-cy-id="analyst-price-target-table">
          <caption>
            {t(
              'page.companyReport.valuation.analystPriceTarget.table.description',
              {
                tickerSymbol,
              }
            )}
          </caption>
          <thead>
            <tr>
              <th>
                {t(
                  'page.companyReport.valuation.analystPriceTarget.table.header.date'
                )}
              </th>
              <th>
                {t(
                  'page.companyReport.valuation.analystPriceTarget.table.header.sharePrice'
                )}
              </th>
              <th>
                <Tooltip
                  openDelay={300}
                  light
                  title={
                    <TipContainer>
                      {getTerm('Average 1Y Price Target')}
                    </TipContainer>
                  }
                >
                  <TooltipName>
                    {t(
                      'page.companyReport.valuation.analystPriceTarget.table.header.average'
                    )}
                  </TooltipName>
                </Tooltip>
              </th>
              <th>
                <Tooltip
                  openDelay={300}
                  light
                  title={<TipContainer>{getTerm('Dispersion')}</TipContainer>}
                >
                  <TooltipName>
                    {t(
                      'page.companyReport.valuation.analystPriceTarget.table.header.dispersion'
                    )}
                  </TooltipName>
                </Tooltip>
              </th>
              <th>
                {t(
                  'page.companyReport.valuation.analystPriceTarget.table.header.high'
                )}
              </th>
              <th>
                {t(
                  'page.companyReport.valuation.analystPriceTarget.table.header.low'
                )}
              </th>
              <th>
                <Tooltip
                  openDelay={300}
                  light
                  title={
                    <TipContainer>{getTerm('1Y Actual Price')}</TipContainer>
                  }
                >
                  <TooltipName>
                    {t(
                      'page.companyReport.valuation.analystPriceTarget.table.header.actual'
                    )}
                  </TooltipName>
                </Tooltip>
              </th>
              <th>
                {t(
                  'page.companyReport.valuation.analystPriceTarget.table.header.analysts'
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {reversePriceTarget.map((priceTarget, i) => {
              const firstRow = i === 0;
              const date = priceTarget[0];
              const price = priceTarget[1];
              const oneYearAgo = subYears(date, 1).getTime();
              const validPrice = getValidPrice(oneYearAgo, priceData, daysBack);
              const actualPrice = getCurrentPrice(date, priceData);
              const { growth, growthSign } = getPriceGrowthData(
                validPrice,
                price,
                valuationData.shouldShowAptCalc
              );
              const growthColor = getGrowthColor(growth, theme);
              return (
                <CompanyReportTableRow
                  selected={firstRow}
                  key={i}
                  data-cy-id={`analyst-table-row-${i}`}
                >
                  <td>
                    {firstRow
                      ? t(
                          'page.companyReport.valuation.analystPriceTarget.table.row.current'
                        )
                      : format(date, 'MMM ’yy')}
                  </td>
                  <td>{formatterCurrency(validPrice, currencyISO)}</td>
                  <td>
                    <Grid gap="10px" templateColumns="auto 55px">
                      <Grid>{formatterCurrency(price, currencyISO)}</Grid>
                      <Grid>
                        <TextColor color={growthColor}>
                          {growthSign + formatterPercentage(growth)}
                        </TextColor>
                      </Grid>
                    </Grid>
                  </td>
                  <td>
                    <TextColor
                      color={
                        analystData[date].dispersion < dispersionThreshold
                          ? theme.color.good
                          : theme.color.bad
                      }
                    >
                      {formatterPercentage(analystData[date].dispersion)}
                    </TextColor>
                  </td>
                  <td>
                    {formatterCurrency(
                      analystData[date].priceTargetMax,
                      currencyISO
                    )}
                  </td>
                  <td>
                    {formatterCurrency(
                      analystData[date].priceTargetMin,
                      currencyISO
                    )}
                  </td>
                  <td>{formatterCurrency(actualPrice, currencyISO)}</td>
                  <td>{analystData[date].analystCount}</td>
                </CompanyReportTableRow>
              );
            })}
          </tbody>
        </AnalystDataTable>
      </TableWrapper>
    </>
  );
};
